<template>
  <div class="aa-page">
    <div class="aa-header">
      <h1 class="aa-title">公告管理</h1>
    </div>

    <!-- 新增公告表單 -->
    <div class="aa-form-card">
      <h2 class="aa-form-title">➕ 新增公告</h2>
      <div class="aa-field">
        <label class="aa-label">標題 <span class="aa-required">*</span></label>
        <input v-model="form.title" class="aa-input" placeholder="請輸入公告標題" maxlength="100" />
      </div>
      <div class="aa-field">
        <label class="aa-label">內容 <span class="aa-required">*</span></label>
        <textarea v-model="form.content" class="aa-textarea" placeholder="請輸入公告內容" rows="12" />
      </div>
      <!-- 測試寄信 -->
      <div class="aa-test-row">
        <input v-model="testEmail" class="aa-input aa-input--test" placeholder="測試信箱（先寄給單一信箱確認）" />
        <button class="aa-btn aa-btn--test" :disabled="testing" @click="sendTest">
          {{ testing ? '寄送中…' : '📧 寄測試信' }}
        </button>
      </div>
      <p v-if="testMsg" class="aa-ok" :class="{ 'aa-error': testError }">{{ testMsg }}</p>

      <p v-if="formError" class="aa-error">{{ formError }}</p>
      <div class="aa-form-actions">
        <button class="aa-btn aa-btn--primary" :disabled="submitting" @click="submit(true)">
          {{ submitting ? '發送中…' : '✅ 發布並寄信給所有店家' }}
        </button>
        <button class="aa-btn aa-btn--secondary" :disabled="submitting" @click="submit(false)">
          📋 僅發布，不寄信
        </button>
      </div>
      <p v-if="submitOk" class="aa-ok">{{ submitOkMsg }}</p>
    </div>

    <!-- 公告列表 -->
    <div class="aa-list-card">
      <h2 class="aa-list-title">
        公告列表
        <span v-if="announcements.length > 0" class="aa-list-count">{{ announcements.length }} 筆</span>
      </h2>
      <div v-if="loading" class="aa-state">載入中…</div>
      <div v-else-if="listError" class="aa-state aa-error">{{ listError }}</div>
      <div v-else-if="announcements.length === 0" class="aa-state">目前沒有公告</div>
      <div v-else class="aa-list">
        <template v-for="a in visibleList" :key="a.id">
        <div
          class="aa-item"
          :class="{ 'aa-item--inactive': !a.is_active && !a.show_frontend }"
          @click="goDetail(a.id)"
        >
          <span class="aa-item__badge" :class="(a.is_active || a.show_frontend) ? 'aa-item__badge--on' : 'aa-item__badge--off'">
            {{ (a.is_active || a.show_frontend) ? '顯示中' : '已停用' }}
          </span>
          <span class="aa-item__title">{{ a.title }}</span>
          <span class="aa-item__date">{{ fmtDate(a.created_at) }}</span>
          <div class="aa-item__actions" @click.stop>
            <button
              class="aa-btn aa-btn--sm"
              :class="a.is_active ? 'aa-btn--on' : 'aa-btn--off'"
              @click="toggle(a)"
              title="顯示後台"
            >
              後台 {{ a.is_active ? '✓' : '✗' }}
            </button>
            <button
              class="aa-btn aa-btn--sm"
              :class="a.show_frontend ? 'aa-btn--on' : 'aa-btn--off'"
              @click="toggleFrontend(a)"
              title="顯示前台"
            >
              前台 {{ a.show_frontend ? '✓' : '✗' }}
            </button>
            <button class="aa-btn aa-btn--sm aa-btn--edit" @click="startEdit(a)">修改</button>
            <button class="aa-btn aa-btn--sm aa-btn--danger" @click="remove(a)">刪除</button>
          </div>
          <span class="aa-item__arrow">→</span>
        </div>
        <!-- inline 編輯區（與 aa-item 同層，在 template v-for 內） -->
        <div v-if="editingId === a.id" class="aa-edit-area" @click.stop>
          <div class="aa-field">
            <label class="aa-label">標題</label>
            <input v-model="editForm.title" class="aa-input" maxlength="100" />
          </div>
          <div class="aa-field">
            <label class="aa-label">內容</label>
            <textarea v-model="editForm.content" class="aa-textarea" rows="12" />
          </div>
          <p v-if="editError" class="aa-error">{{ editError }}</p>
          <div class="aa-edit-actions">
            <button class="aa-btn aa-btn--primary aa-btn--sm" :disabled="editSaving" @click="saveEdit(a)">
              {{ editSaving ? '儲存中…' : '✅ 儲存修改' }}
            </button>
            <button class="aa-btn aa-btn--secondary aa-btn--sm" @click="editingId = null">取消</button>
          </div>
        </div>
        </template>
        <div v-if="hasMore && !showAll" class="aa-more" @click="showAll = true">
          更多公告 ↓
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()

function goDetail(id) {
  router.push({ name: 'AdminAnnouncementDetail', params: { id } })
}

const announcements = ref([])
const loading       = ref(false)
const submitting    = ref(false)
const formError     = ref('')
const submitOk      = ref(false)
const submitOkMsg   = ref('')
const lastSent      = ref(0)
const form          = ref({ title: '', content: '' })
const testEmail     = ref('')
const testing       = ref(false)
const testMsg       = ref('')
const testError     = ref(false)

const listError = ref('')
const showAll   = ref(false)
const PAGE_SIZE = 5
const visibleList = computed(() =>
  showAll.value ? announcements.value : announcements.value.slice(0, PAGE_SIZE)
)
const hasMore = computed(() => announcements.value.length > PAGE_SIZE)

async function fetchList() {
  loading.value = true
  listError.value = ''
  try {
    const { data } = await axios.get('/api/admin/booking/announcements')
    announcements.value = data.announcements || []
  } catch (e) {
    listError.value = `載入失敗：${e.response?.status || ''} ${e.response?.data?.message || e.message}`
  } finally {
    loading.value = false
  }
}

async function sendTest() {
  testMsg.value   = ''
  testError.value = false
  if (!form.value.title.trim() || !form.value.content.trim()) {
    testMsg.value   = '請先填寫標題與內容'
    testError.value = true
    return
  }
  if (!testEmail.value.trim()) {
    testMsg.value   = '請輸入測試信箱'
    testError.value = true
    return
  }
  testing.value = true
  try {
    await axios.post('/api/admin/booking/announcements/test', {
      title:   form.value.title,
      content: form.value.content,
      email:   testEmail.value.trim(),
    })
    testMsg.value = `✅ 測試信已寄出至 ${testEmail.value.trim()}，請確認收信`
  } catch (e) {
    testMsg.value   = e.response?.data?.message || '寄送失敗'
    testError.value = true
  } finally {
    testing.value = false
  }
}

async function submit(sendEmail = true) {
  formError.value = ''
  submitOk.value  = false
  if (!form.value.title.trim() || !form.value.content.trim()) {
    formError.value = '標題與內容不得為空'
    return
  }
  submitting.value = true
  try {
    const { data } = await axios.post('/api/admin/booking/announcements', { ...form.value, sendEmail })
    lastSent.value  = data.emailSent || 0
    submitOk.value  = true
    submitOkMsg.value = sendEmail
      ? '✅ 公告已發布，通知信件寄送中（背景執行）'
      : '✅ 公告已發布（未寄信）'
    form.value      = { title: '', content: '' }
  } catch (e) {
    formError.value = e.response?.data?.message || '發送失敗，請稍後再試'
  } finally {
    submitting.value = false
    await fetchList().catch(() => {})
  }
}

async function toggle(a) {
  await axios.put(`/api/admin/booking/announcements/${a.id}`)
  a.is_active = !a.is_active
}

async function toggleFrontend(a) {
  await axios.put(`/api/admin/booking/announcements/${a.id}/toggle-frontend`)
  a.show_frontend = !a.show_frontend
}

// ── 修改公告（不寄信）─────────────────────────────────
const editingId  = ref(null)
const editForm   = ref({ title: '', content: '' })
const editSaving = ref(false)
const editError  = ref('')

function startEdit(a) {
  editingId.value = a.id
  editForm.value  = { title: a.title, content: a.content || '' }
  editError.value = ''
}

async function saveEdit(a) {
  editError.value = ''
  if (!editForm.value.title.trim() || !editForm.value.content.trim()) {
    editError.value = '標題與內容不得為空'
    return
  }
  editSaving.value = true
  try {
    await axios.patch(`/api/admin/booking/announcements/${a.id}`, editForm.value)
    a.title   = editForm.value.title.trim()
    a.content = editForm.value.content.trim()
    editingId.value = null
  } catch (e) {
    editError.value = e.response?.data?.message || '儲存失敗'
  } finally {
    editSaving.value = false
  }
}

async function remove(a) {
  if (!confirm(`確定刪除公告「${a.title}」？`)) return
  await axios.delete(`/api/admin/booking/announcements/${a.id}`)
  announcements.value = announcements.value.filter(x => x.id !== a.id)
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''
}

onMounted(fetchList)
</script>

<style scoped>
.aa-page { padding: 32px 24px 80px; max-width: 800px; }
.aa-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; }
.aa-title { font-size: 1.6rem; color: #333; margin: 0; }

.aa-form-card, .aa-list-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e0da;
  padding: 24px;
  margin-bottom: 24px;
}
.aa-form-title { font-size: 1.05rem; font-weight: 600; color: #555; margin: 0 0 18px; }

.aa-list-toggle {
  font-size: 1.05rem; font-weight: 600; color: #555;
  cursor: pointer; list-style: none; display: flex; align-items: center; gap: 8px;
  margin-bottom: 0;
}
.aa-list-toggle::-webkit-details-marker { display: none; }
.aa-list-toggle::after { content: ' ▼'; font-size: 0.75rem; color: #aaa; }
details[open] .aa-list-toggle::after { content: ' ▲'; }
details[open] .aa-list-toggle { margin-bottom: 18px; }
.aa-list-count {
  background: #8b6f5c; color: #fff; border-radius: 20px;
  font-size: 0.75rem; padding: 2px 8px; font-weight: 600;
}

.aa-field { margin-bottom: 14px; }
.aa-label { display: block; font-size: 0.88rem; color: #666; margin-bottom: 6px; }
.aa-required { color: #e74c3c; }
.aa-input, .aa-textarea {
  width: 100%; padding: 10px 14px; border: 1px solid #ddd; border-radius: 8px;
  font-size: 0.95rem; font-family: inherit; box-sizing: border-box;
}
.aa-textarea { resize: vertical; min-height: 220px; }
.aa-input:focus, .aa-textarea:focus { outline: none; border-color: #8b6f5c; }

.aa-test-row { display: flex; gap: 8px; margin-top: 14px; }
.aa-input--test { flex: 1; }
.aa-btn--test { background: #3498db; color: #fff; white-space: nowrap; }
.aa-btn--test:hover:not(:disabled) { background: #2980b9; }
.aa-form-actions { margin-top: 16px; display: flex; gap: 10px; flex-wrap: wrap; }
.aa-btn--secondary { background: #f4f6f9; border: 1px solid #ddd; color: #555; }
.aa-btn--secondary:hover:not(:disabled) { background: #e8ecf0; }
.aa-error { color: #c0392b; font-size: 0.88rem; margin-top: 8px; }
.aa-ok    { color: #27ae60; font-size: 0.88rem; margin-top: 10px; }

/* 按鈕 */
.aa-btn {
  display: inline-flex; align-items: center; padding: 10px 20px;
  border-radius: 8px; border: none; font-size: 0.95rem; font-weight: 600;
  cursor: pointer; transition: opacity 0.15s;
}
.aa-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.aa-btn--primary { background: #8b6f5c; color: #fff; }
.aa-btn--primary:hover:not(:disabled) { background: #7a5f4d; }
.aa-btn--sm { padding: 6px 14px; font-size: 0.82rem; }
.aa-btn--ok { background: #27ae60; color: #fff; }
.aa-btn--warn { background: #e67e22; color: #fff; }
.aa-btn--danger { background: #e74c3c; color: #fff; }
.aa-btn--on  { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
.aa-btn--off { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }

/* 列表 */
.aa-list-title {
  font-size: 1.05rem; font-weight: 600; color: #555;
  margin: 0 0 16px; display: flex; align-items: center; gap: 8px;
}
.aa-state { color: #aaa; padding: 24px 0; text-align: center; }

.aa-list {
  border: 1px solid #e8e0da;
  border-radius: 10px;
  overflow: hidden;
}
.aa-item {
  display: flex; align-items: center; gap: 10px;
  padding: 13px 16px; border-bottom: 1px solid #f0f0f0;
  cursor: pointer; transition: background 0.15s;
}
.aa-item:last-child { border-bottom: none; }
.aa-item:hover { background: #fafafa; }
.aa-item--inactive { opacity: 0.5; }

.aa-item__badge { flex-shrink: 0; font-size: 0.72rem; font-weight: 600; padding: 2px 8px; border-radius: 20px; }
.aa-item__badge--on  { background: #d4edda; color: #155724; }
.aa-item__badge--off { background: #f8d7da; color: #721c24; }
.aa-item__title { flex: 1; font-size: 0.92rem; font-weight: 600; color: #333; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.aa-item__date  { flex-shrink: 0; font-size: 0.78rem; color: #aaa; }
.aa-item__actions { flex-shrink: 0; display: flex; gap: 6px; }
.aa-item__arrow { flex-shrink: 0; font-size: 0.8rem; color: #bbb; }

.aa-more {
  padding: 13px 16px;
  text-align: center;
  font-size: 0.88rem;
  font-weight: 600;
  color: #8b6f5c;
  cursor: pointer;
  transition: background 0.15s;
  border-top: 1px solid #f0f0f0;
}
.aa-more:hover { background: #fdf8f5; }

/* 修改按鈕 */
.aa-btn--edit { background: #e8f4fd; color: #1a6b9a; border: 1px solid #b8d9ef; }
.aa-btn--edit:hover { background: #d0eaf8; }

/* inline 編輯區 */
.aa-edit-area {
  padding: 16px 20px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}
.aa-edit-actions { display: flex; gap: 8px; margin-top: 10px; }
</style>
